import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Button, CustomModal, CustomTextArea } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import { CircleCloseIcon, NavCloseIcon, UploadIcon } from 'assets/icons';
import { FILE_TYPE, FILE_UPLOAD_SIZE } from 'constants/common';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam, selectMerchantCustomizations } from 'containers/app/appSlice';
import { showNotifier } from 'containers/app/appSlice';
import { useAppDispatch } from 'hooks';
import { NotifierTypes } from 'constants/notifierConstants';
import { logAnalyticEvent } from 'utils/analytics';
import { clevertapEvents } from 'types/baseTypes';
import MultiFileUpload from 'components/multi-file-upload/MultiFileUpload';
import { UploadAttachmentFile } from 'types/baseTypes';
import { PRESIGNED_UPLOAD_USE_CASE } from 'constants/common';
import AutoFillLoadingView from './AutoFillLoadingView';

const MAX_FILE_SIZE = FILE_UPLOAD_SIZE.SIZE_8_MB_BYTES;
const MAX_TOTAL_FILES_DEFAULT = 5;

export interface AutoFillAttachment {
  fileKey: string;
  fileType: string;
  fileName: string;
}

export type OnSaveAutoFill = (content: string, attachments: AutoFillAttachment[]) => Promise<void>;

export interface AutoFillGenericModalTexts {
  title: string;
  description: string;
  uploadSubText?: string;
  instructionsLabel?: string;
  instructionsPlaceholder?: string;
  loadingTitle?: string;
  loadingTips?: string[];
  successMessage?: string;
}

interface AutoFillGenericModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: OnSaveAutoFill;
  isSaving: boolean;
  texts: AutoFillGenericModalTexts;
  enableFileUpload?: boolean;
  uploadUseCase?: PRESIGNED_UPLOAD_USE_CASE;
  maxTextLength?: number;
  showTextAreaCharCount?: boolean;
  onError?: (error: Error) => void;
}

const AutoFillGenericModal: FC<AutoFillGenericModalProps> = ({
  isOpen,
  onClose,
  onSave,
  isSaving,
  texts,
  enableFileUpload = true,
  uploadUseCase = PRESIGNED_UPLOAD_USE_CASE.INVOICE_AUTO_FILL,
  maxTextLength = 1000,
  showTextAreaCharCount = true,
  onError
}) => {
  const [text, setText] = useState('');
  const [files, setFiles] = useState<UploadAttachmentFile[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const currentTeam = useAppSelector(selectCurrentTeam);
  const merchantCustomizations = useAppSelector(selectMerchantCustomizations);
  const dispatch = useAppDispatch();
  const uploadAbortControllerRef = useRef<AbortController | null>(null);

  const maxTotalFiles = merchantCustomizations?.limitConfigs?.autofillAttachmentLimit ?? MAX_TOTAL_FILES_DEFAULT;
  const shouldEnableFileUpload = enableFileUpload && merchantCustomizations?.limitConfigs?.autofillAttachmentLimit > 0;

  // Recreate AbortController when modal opens so uploads work after close → reopen.
  // If we only created once on mount, closing the modal aborts the controller and
  // it stays aborted when reopened, so new uploads would abort and no file would appear.
  useEffect(() => {
    if (!isOpen) return;

    uploadAbortControllerRef.current = new AbortController();

    return () => {
      if (uploadAbortControllerRef.current) {
        uploadAbortControllerRef.current.abort();
        uploadAbortControllerRef.current = null;
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      logAnalyticEvent(clevertapEvents.webAutofillModalDisplay, {
        action: 'opened',
        entityType: uploadUseCase.toLowerCase(),
        teamId: currentTeam.id.toString()
      });
    }
  }, [isOpen]);

  const handleTextChange = (value: string) => {
    setText(value);
  };

  const handleRemoveFile = useCallback((fileId: number | string) => {
    setFiles(prev => prev.filter(f => (f.id || f.tempId) !== fileId));
  }, []);

  const handleIsFilesUploading = useCallback((value: boolean) => {
    setErrorMessage('');
    setIsUploading(value);
  }, []);

  const handleProcess = useCallback(async () => {
    if (!text.trim() && (shouldEnableFileUpload ? files.length === 0 : true)) {
      dispatch(
        showNotifier({
          message: {
            primaryMessage: shouldEnableFileUpload ? 'Please enter text or upload files' : 'Please enter text',
            secondaryMessage: ''
          },
          type: NotifierTypes.INFO,
          showClose: false
        })
      );
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      const attachments =
        shouldEnableFileUpload && files?.length > 0
          ? files?.map(file => ({
              fileKey: file?.url,
              fileType: file?.fileType,
              fileName: file?.title
            }))
          : [];

      await onSave(text.trim(), attachments);

      logAnalyticEvent(clevertapEvents.webAutofillSubmit, {
        status: 'success',
        entityType: uploadUseCase.toLowerCase(),
        text: text.trim(),
        hasAttachments: attachments?.length > 0,
        teamId: currentTeam.id.toString()
      });

      setText('');
      setFiles([]);
      setErrorMessage('');
      onClose();

      if (texts.successMessage) {
        dispatch(
          showNotifier({
            message: {
              primaryMessage: texts.successMessage,
              secondaryMessage: ''
            },
            type: NotifierTypes.SUCCESS,
            showClose: true
          })
        );
      }
    } catch (error: unknown) {
      const errorData = (error as { data?: { message?: string; details?: string; description?: string } })?.data;
      const extractedErrorMessage =
        errorData?.details || errorData?.description || 'Failed to autofill. Please try again.';

      logAnalyticEvent(clevertapEvents.webAutofillSubmit, {
        status: 'error',
        errorMessage: extractedErrorMessage,
        entityType: uploadUseCase.toLowerCase(),
        text: text.trim(),
        hasAttachments: files?.length > 0,
        teamId: currentTeam.id.toString()
      });

      logAnalyticEvent(clevertapEvents.webAutofillSubmit, {
        status: 'error',
        errorMessage: extractedErrorMessage,
        entityType: uploadUseCase.toLowerCase(),
        text: text.trim(),
        hasAttachments: files?.length > 0,
        teamId: currentTeam.id.toString()
      });

      setErrorMessage(extractedErrorMessage);

      if (onError) {
        onError(error instanceof Error ? error : new Error(String(error)));
      }
    } finally {
      setIsProcessing(false);
    }
  }, [text, files, onSave, onError, dispatch, onClose, texts, shouldEnableFileUpload]);

  const isLoading = isProcessing || isUploading;

  const handleClose = useCallback(() => {
    logAnalyticEvent(clevertapEvents.webAutofillModalDisplay, {
      action: 'closed',
      entityType: uploadUseCase.toLowerCase(),
      text: text.trim(),
      hasAttachments: files?.length > 0,
      teamId: currentTeam.id.toString()
    });
    if (uploadAbortControllerRef.current) {
      uploadAbortControllerRef.current.abort();
    }
    setText('');
    setFiles([]);
    setErrorMessage('');
    onClose();
  }, [onClose]);

  const showLoadingView = isSaving;
  const isContinueButtonDisabled = isLoading || (!text.trim() && (shouldEnableFileUpload ? files.length === 0 : true));

  return (
    <CustomModal
      open={isOpen}
      closeModal={handleClose}
      width="md:w-[550px]"
      height={`${showLoadingView ? 'md:min-h-[364px]' : 'md:min-h-[521px]'} md:max-h-[775px]`}
      enableOutsideClick={false}
      className="max-h-[80vh] overflow-hidden">
      <div className="relative flex h-full flex-col">
        {!showLoadingView && (
          <CircleCloseIcon
            className="absolute top-2 right-2 z-[1] shrink-0 cursor-pointer"
            onClick={handleClose}
            id="auto-fill-close-icon"
          />
        )}
        {showLoadingView ? (
          <AutoFillLoadingView title={texts.loadingTitle} tips={texts.loadingTips} />
        ) : (
          <>
            <div className="shrink-0 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="mb-[6px] text-xl font-bold text-headingGray">{texts.title}</h2>
                  <p className="text-sbase font-medium text-headingGray">{texts.description}</p>
                </div>
              </div>
              {errorMessage && (
                <div
                  className="mt-4 flex flex-row items-center justify-between gap-4 rounded-md border border-secondary
                    bg-[#A4D7FA33] px-3 py-2 shadow-md"
                  role="alert"
                  aria-label="Error message">
                  <div className="flex min-w-0 flex-1 flex-row items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-sbase font-medium text-primaryText">{errorMessage}</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setErrorMessage('')}
                    className="flex shrink-0 cursor-pointer items-center justify-center p-2"
                    tabIndex={0}
                    aria-label="Close error message">
                    <NavCloseIcon className="h-3 w-3" />
                  </button>
                </div>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-6">
              {shouldEnableFileUpload && (
                <div className="mb-6">
                  <div className="mb-2 text-sbase font-semibold text-primaryText">
                    {files?.length > 0 ? `Uploaded files (${files.length})` : 'Upload files'}
                  </div>
                  <MultiFileUpload
                    files={files}
                    setFiles={setFiles}
                    id="auto-fill-file-upload"
                    maxFileSize={MAX_FILE_SIZE}
                    maxFiles={maxTotalFiles}
                    FileUploadIcon={UploadIcon}
                    onFileRemove={handleRemoveFile}
                    teamId={currentTeam.id.toString()}
                    useCase={uploadUseCase}
                    supportedFileTypes={{
                      [FILE_TYPE.PDF]: ['.pdf'],
                      [FILE_TYPE.PNG]: ['.png'],
                      [FILE_TYPE.JPEG]: ['.jpg', '.jpeg']
                    }}
                    isUploading={isUploading}
                    setIsUploading={handleIsFilesUploading}
                    uploadToPresigned={true}
                    uploadAbortSignal={uploadAbortControllerRef.current?.signal}
                    subText={
                      texts.uploadSubText ||
                      `PDF, PNG or JPG up to ${Math.round(
                        MAX_FILE_SIZE / FILE_UPLOAD_SIZE.SIZE_1_MB
                      )}MB (max ${maxTotalFiles} files)`
                    }
                    enableFilePreview={true}
                    allowPaste={true}
                  />
                </div>
              )}
              <div className="mb-6">
                <div className="mb-2 text-sbase font-semibold text-primaryText">
                  {texts.instructionsLabel || 'Need to add instructions ? (Optional)'}
                </div>
                <CustomTextArea
                  id="auto-fill-text-input"
                  name="auto-fill-text"
                  placeholder={texts.instructionsPlaceholder || 'Enter text here'}
                  value={text}
                  onChange={handleTextChange}
                  className="!h-[113px] max-h-[113px] border border-borderGray outline-none"
                  disabled={isSaving}
                  autoFocus={true}
                  showRemainingChar={showTextAreaCharCount}
                  maxLength={maxTextLength}
                  remainingCharClassName="!text-left"
                />
              </div>
            </div>

            <div className="shrink-0 px-6 pt-4 pb-6">
              <div className="flex justify-end gap-3">
                <Button
                  onClick={handleClose}
                  type={ButtonType.SECONDARY}
                  className="h-[38px] w-20"
                  id="cancel-auto-fill-button">
                  Cancel
                </Button>
                <Button
                  onClick={handleProcess}
                  type={ButtonType.PRIMARY}
                  isLoading={isSaving}
                  disabled={isContinueButtonDisabled}
                  className="h-[38px] w-20"
                  id="continue-auto-fill-button">
                  Continue
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </CustomModal>
  );
};

export default AutoFillGenericModal;
